import {
    Button,
    DrawerProps,
    Group,
    Select,
    Stack,
} from '@mantine/core';
import {PlatformDrawer} from '@flexinet/ui-components';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    CustomersFilters,
    useCustomersFilter,
} from '../hooks/useCustomersFilter';

/**
 * FNP-918: the customers filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (useCustomersFilter), not component state, so the table —
 * a sibling, not a child — filters the same list.
 *
 * ⚠️ Only `delinquent` is offered. The API returns no enum field other than
 * this one, and a filter control for a free-text field would duplicate the
 * keyword search. Adding filters for fields the operator cannot meaningfully
 * pick from would be building for a hypothetical use case.
 *
 * ⚠️ Filtering is CLIENT-SIDE over the loaded window — `listCustomers` takes no
 * filter argument. See useCustomers.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useCustomersFilter();

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Customers')}
            size='md'
            {...rest}
        >
            <Formik<CustomersFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Delinquent')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'true',
                                        label: I18n.get('Delinquent'),
                                    },
                                    {
                                        value: 'false',
                                        label: I18n.get('Current'),
                                    },
                                ]}
                                value={values.delinquent ?? null}
                                onChange={(value) =>
                                    setFieldValue('delinquent', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </PlatformDrawer>
    );
};
